import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uniqueArray } from './util/unique-array';
var initialPlacementState = {
  currentlyMounted: [],
  hasMounted: []
};
var PostOfficeScreenContext = /*#__PURE__*/createContext({
  placements: initialPlacementState
});

/**
 * This hook provides access to the PostOfficeScreen context, which provides information about
 * the state of the screen under the PostOfficeProvider. This can be used for coordination between placements.
 *
 * @returns information about the screen state
 * @property {object} placements - Information about placements on screen
 * @property {Array<string>} placements.currentlyMounted - An array of placementIds for placements that are currently mounted.
 * @property {Array<string>} placements.hasMounted - An array of placementIds for placements that have been mounted at least once.
 *
 **/
export var usePostOfficeScreen = function usePostOfficeScreen() {
  return useContext(PostOfficeScreenContext);
};
var PostOfficeScreenUpdateContext = /*#__PURE__*/createContext({
  placementMount: function placementMount() {},
  placementUnmount: function placementUnmount() {}
});
export var usePostOfficeScreenUpdateContext = function usePostOfficeScreenUpdateContext() {
  return useContext(PostOfficeScreenUpdateContext);
};
export var PostOfficeScreenContextProvider = function PostOfficeScreenContextProvider(_ref) {
  var children = _ref.children;
  var _useState = useState(initialPlacementState),
    _useState2 = _slicedToArray(_useState, 2),
    placements = _useState2[0],
    setPlacements = _useState2[1];
  var placementMount = useCallback(function (_ref2) {
    var placementId = _ref2.placementId;
    return setPlacements(function (state) {
      return state.currentlyMounted.includes(placementId) ? state : _objectSpread(_objectSpread({}, state), {}, {
        currentlyMounted: uniqueArray([].concat(_toConsumableArray(state.currentlyMounted), [placementId])),
        hasMounted: uniqueArray([].concat(_toConsumableArray(state.hasMounted), [placementId]))
      });
    });
  }, []);
  var placementUnmount = useCallback(function (_ref3) {
    var placementId = _ref3.placementId;
    return setPlacements(function (state) {
      return !state.currentlyMounted.includes(placementId) ? state : _objectSpread(_objectSpread({}, state), {}, {
        currentlyMounted: state.currentlyMounted.filter(function (e) {
          return e !== placementId;
        })
      });
    });
  }, []);
  var postOfficeScreenUpdateContext = useMemo(function () {
    return {
      placementMount: placementMount,
      placementUnmount: placementUnmount
    };
  }, [placementMount, placementUnmount]);
  var postOfficeScreenContext = useMemo(function () {
    return {
      placements: placements
    };
  }, [placements]);
  return /*#__PURE__*/React.createElement(PostOfficeScreenUpdateContext.Provider, {
    value: postOfficeScreenUpdateContext
  }, /*#__PURE__*/React.createElement(PostOfficeScreenContext.Provider, {
    value: postOfficeScreenContext
  }, children));
};
export var usePostOfficeScreenPlacement = function usePostOfficeScreenPlacement(_ref4) {
  var placementId = _ref4.placementId;
  var _usePostOfficeScreenU = usePostOfficeScreenUpdateContext(),
    placementMount = _usePostOfficeScreenU.placementMount,
    placementUnmount = _usePostOfficeScreenU.placementUnmount;
  useEffect(function () {
    if (!placementId) {
      return;
    }
    placementMount({
      placementId: placementId
    });
    return function () {
      placementUnmount({
        placementId: placementId
      });
    };
  }, [placementId, placementMount, placementUnmount]);
};